import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Building2, Landmark, Plus, Search, Edit, Trash2, Globe, Phone, Mail, MapPin,
  RefreshCw, TrendingUp, DollarSign, Home, Calendar, Award, ChevronRight,
  Building, Users, FileText, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeveloperMaster = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('developers');
  
  // Developer state
  const [developers, setDevelopers] = useState([]);
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [showDeveloperDialog, setShowDeveloperDialog] = useState(false);
  const [editingDeveloper, setEditingDeveloper] = useState(null);
  const [developerSearch, setDeveloperSearch] = useState('');
  
  // Project state
  const [projects, setProjects] = useState([]);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  
  // Summary
  const [summary, setSummary] = useState(null);
  const [topDevelopers, setTopDevelopers] = useState([]);
  
  // Form state for developer
  const [developerForm, setDeveloperForm] = useState({
    name: '', company_type: 'Private Limited', email: '', phone: '',
    website: '', address: '', city: '', state: '', rera_number: '',
    established_year: '', total_projects: 0,
    contact_person: '', contact_person_phone: '', contact_person_email: '',
    notes: ''
  });
  
  // Form state for project
  const [projectForm, setProjectForm] = useState({
    name: '', project_type: 'Residential', location: '', city: '',
    total_units: '', available_units: '', price_range_min: '', price_range_max: '',
    possession_date: '', rera_number: '', status: 'ongoing', description: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Progressive — each tile paints as it arrives.
  const fetchData = () => {
    setLoading(true);
    const tasks = [
      axios.get(`${API}/developers?token=${token}`)
        .then((r) => setDevelopers(r.data.developers || []))
        .catch((e) => console.error('[DeveloperMaster] developers failed:', e)),
      axios.get(`${API}/developers/summary?token=${token}`)
        .then((r) => setSummary(r.data.summary))
        .catch((e) => console.error('[DeveloperMaster] summary failed:', e)),
      axios.get(`${API}/developers/top?token=${token}`)
        .then((r) => setTopDevelopers(r.data.developers || []))
        .catch((e) => console.error('[DeveloperMaster] top failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const fetchProjects = async (developerId) => {
    try {
      const response = await axios.get(`${API}/developers/${developerId}/projects?token=${token}`);
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Developer handlers
  const handleSaveDeveloper = async () => {
    try {
      const payload = {
        ...developerForm,
        established_year: developerForm.established_year ? parseInt(developerForm.established_year) : null,
        total_projects: parseInt(developerForm.total_projects) || 0
      };
      
      if (editingDeveloper) {
        await axios.put(`${API}/developers/${editingDeveloper.id}?token=${token}`, payload);
        toast.success('Developer updated successfully');
      } else {
        await axios.post(`${API}/developers?token=${token}`, payload);
        toast.success('Developer created successfully');
      }
      setShowDeveloperDialog(false);
      resetDeveloperForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save developer');
    }
  };

  const handleDeleteDeveloper = async (developerId) => {
    if (!window.confirm('Are you sure you want to delete this developer?')) return;
    try {
      await axios.delete(`${API}/developers/${developerId}?token=${token}`);
      toast.success('Developer deleted');
      fetchData();
      if (selectedDeveloper?.id === developerId) {
        setSelectedDeveloper(null);
        setProjects([]);
      }
    } catch (error) {
      toast.error('Failed to delete developer');
    }
  };

  const resetDeveloperForm = () => {
    setDeveloperForm({
      name: '', company_type: 'Private Limited', email: '', phone: '',
      website: '', address: '', city: '', state: '', rera_number: '',
      established_year: '', total_projects: 0,
      contact_person: '', contact_person_phone: '', contact_person_email: '',
      notes: ''
    });
    setEditingDeveloper(null);
  };

  // Project handlers
  const handleSaveProject = async () => {
    if (!selectedDeveloper) return;
    try {
      const payload = {
        ...projectForm,
        total_units: parseInt(projectForm.total_units) || 0,
        available_units: parseInt(projectForm.available_units) || 0,
        price_range_min: parseFloat(projectForm.price_range_min) || 0,
        price_range_max: parseFloat(projectForm.price_range_max) || 0
      };
      
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}?token=${token}`, payload);
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${API}/developers/${selectedDeveloper.id}/projects?token=${token}`, payload);
        toast.success('Project created successfully');
      }
      setShowProjectDialog(false);
      resetProjectForm();
      fetchProjects(selectedDeveloper.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}?token=${token}`);
      toast.success('Project deleted');
      if (selectedDeveloper) fetchProjects(selectedDeveloper.id);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const resetProjectForm = () => {
    setProjectForm({
      name: '', project_type: 'Residential', location: '', city: '',
      total_units: '', available_units: '', price_range_min: '', price_range_max: '',
      possession_date: '', rera_number: '', status: 'ongoing', description: ''
    });
    setEditingProject(null);
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `AED ${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `AED ${(amount / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredDevelopers = developers.filter(d => 
    d.name?.toLowerCase().includes(developerSearch.toLowerCase()) ||
    d.city?.toLowerCase().includes(developerSearch.toLowerCase())
  );

  const projectStatusColors = {
    ongoing: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    upcoming: 'bg-amber-100 text-amber-700',
    sold_out: 'bg-purple-100 text-purple-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="developer-master-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-primary">Developer Master</h1>
          <p className="text-slate-500">Manage real estate developers and their projects</p>
        </div>
        <Button onClick={() => { resetDeveloperForm(); setShowDeveloperDialog(true); }} className="bg-accent hover:bg-accent/90">
          <Plus className="mr-2" size={16} />
          Add Developer
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Developers</p>
                <p className="text-2xl font-bold">{summary?.total_developers || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Landmark className="text-blue-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Projects</p>
                <p className="text-2xl font-bold">{summary?.total_projects || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Building className="text-green-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Sales</p>
                <p className="text-2xl font-bold">{summary?.total_sales || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Sale Value</p>
                <p className="text-2xl font-bold">{formatCurrency(summary?.total_sale_value)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <DollarSign className="text-amber-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="developers">Developers & Projects</TabsTrigger>
          <TabsTrigger value="leaderboard">Top Developers</TabsTrigger>
        </TabsList>

        {/* Developers & Projects Tab */}
        <TabsContent value="developers" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Developers List */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Developers ({developers.length})</CardTitle>
                </div>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search developers..."
                    value={developerSearch}
                    onChange={(e) => setDeveloperSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {filteredDevelopers.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Landmark className="mx-auto mb-2 opacity-50" size={32} />
                    <p>No developers found</p>
                    <Button variant="link" onClick={() => setShowDeveloperDialog(true)}>
                      Add your first developer
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredDevelopers.map((developer) => (
                      <div
                        key={developer.id}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                          selectedDeveloper?.id === developer.id 
                            ? 'border-accent bg-accent/5' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => {
                          setSelectedDeveloper(developer);
                          fetchProjects(developer.id);
                        }}
                        data-testid={`developer-card-${developer.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{developer.name}</h3>
                              {developer.rera_number && (
                                <Badge variant="outline" className="text-xs">
                                  RERA
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                              {developer.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={12} /> {developer.city}
                                </span>
                              )}
                              {developer.established_year && (
                                <span>Est. {developer.established_year}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Building size={12} /> {developer.total_projects} projects
                              </span>
                              <span className="flex items-center gap-1">
                                <TrendingUp size={12} /> {developer.total_sales} sales
                              </span>
                              {developer.total_sale_value > 0 && (
                                <span className="text-green-600 font-medium">
                                  {formatCurrency(developer.total_sale_value)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingDeveloper(developer);
                                setDeveloperForm({
                                  ...developer,
                                  established_year: developer.established_year || ''
                                });
                                setShowDeveloperDialog(true);
                              }}
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDeveloper(developer.id);
                              }}
                            >
                              <Trash2 size={14} />
                            </Button>
                            <ChevronRight size={16} className="text-slate-400" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Projects List */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {selectedDeveloper ? `Projects - ${selectedDeveloper.name}` : 'Select a Developer'}
                  </CardTitle>
                  {selectedDeveloper && (
                    <Button
                      size="sm"
                      onClick={() => { resetProjectForm(); setShowProjectDialog(true); }}
                    >
                      <Plus size={14} className="mr-1" /> Add Project
                    </Button>
                  )}
                </div>
                {selectedDeveloper && (
                  <div className="flex gap-4 mt-2 text-sm text-slate-500">
                    {selectedDeveloper.website && (
                      <a 
                        href={selectedDeveloper.website.startsWith('http') ? selectedDeveloper.website : `https://${selectedDeveloper.website}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-accent hover:underline"
                      >
                        <Globe size={14} /> Website
                      </a>
                    )}
                    {selectedDeveloper.phone && (
                      <span className="flex items-center gap-1">
                        <Phone size={14} /> {selectedDeveloper.phone}
                      </span>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {!selectedDeveloper ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building className="mx-auto mb-2 opacity-50" size={32} />
                    <p>Select a developer to view projects</p>
                  </div>
                ) : projects.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building className="mx-auto mb-2 opacity-50" size={32} />
                    <p>No projects found</p>
                    <Button variant="link" onClick={() => setShowProjectDialog(true)}>
                      Add first project
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className="p-4 rounded-lg border border-slate-200 hover:border-slate-300"
                        data-testid={`project-card-${project.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{project.name}</h3>
                              <Badge className={projectStatusColors[project.status] || 'bg-slate-100'}>
                                {project.status?.replace('_', ' ')}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Home size={12} /> {project.project_type}
                              </span>
                              {project.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={12} /> {project.city}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs">
                              {project.total_units > 0 && (
                                <span className="text-slate-500">
                                  Units: <span className="font-medium">{project.available_units}/{project.total_units}</span> available
                                </span>
                              )}
                              {project.price_range_min > 0 && (
                                <span className="text-green-600">
                                  {formatCurrency(project.price_range_min)} - {formatCurrency(project.price_range_max)}
                                </span>
                              )}
                            </div>
                            {project.possession_date && (
                              <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                                <Calendar size={12} /> Possession: {project.possession_date}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingProject(project);
                                setProjectForm({
                                  ...project,
                                  total_units: project.total_units || '',
                                  available_units: project.available_units || '',
                                  price_range_min: project.price_range_min || '',
                                  price_range_max: project.price_range_max || ''
                                });
                                setShowProjectDialog(true);
                              }}
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteProject(project.id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award size={20} />
                Top Developers by Sale Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topDevelopers.length === 0 ? (
                <p className="text-center py-4 text-slate-500">No sales data yet</p>
              ) : (
                <div className="space-y-3">
                  {topDevelopers.map((developer, index) => (
                    <div key={developer.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{developer.name}</p>
                          <p className="text-sm text-slate-500">
                            {developer.city} • {developer.total_projects} projects
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{formatCurrency(developer.total_sale_value)}</p>
                        <p className="text-sm text-slate-500">{developer.total_sales} sales</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Developer Dialog */}
      <Dialog open={showDeveloperDialog} onOpenChange={setShowDeveloperDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDeveloper ? 'Edit Developer' : 'Add New Developer'}</DialogTitle>
            <DialogDescription>
              {editingDeveloper ? 'Update developer details' : 'Add a new real estate developer'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Developer Name *</Label>
                <Input
                  value={developerForm.name}
                  onChange={(e) => setDeveloperForm({...developerForm, name: e.target.value})}
                  placeholder="e.g., Prestige Group"
                  data-testid="developer-name-input"
                />
              </div>
              <div>
                <Label>Company Type</Label>
                <Select
                  value={developerForm.company_type}
                  onValueChange={(value) => setDeveloperForm({...developerForm, company_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Private Limited">Private Limited</SelectItem>
                    <SelectItem value="Public Limited">Public Limited</SelectItem>
                    <SelectItem value="LLP">LLP</SelectItem>
                    <SelectItem value="Partnership">Partnership</SelectItem>
                    <SelectItem value="Proprietorship">Proprietorship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={developerForm.city}
                  onChange={(e) => setDeveloperForm({...developerForm, city: e.target.value})}
                  placeholder="e.g., Bangalore"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={developerForm.state}
                  onChange={(e) => setDeveloperForm({...developerForm, state: e.target.value})}
                  placeholder="e.g., Karnataka"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>RERA Registration Number</Label>
                <Input
                  value={developerForm.rera_number}
                  onChange={(e) => setDeveloperForm({...developerForm, rera_number: e.target.value})}
                  placeholder="RERA number"
                />
              </div>
              <div>
                <Label>Established Year</Label>
                <Input
                  type="number"
                  value={developerForm.established_year}
                  onChange={(e) => setDeveloperForm({...developerForm, established_year: e.target.value})}
                  placeholder="e.g., 1986"
                />
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={developerForm.phone}
                  onChange={(e) => setDeveloperForm({...developerForm, phone: e.target.value})}
                  placeholder="+91 80 1234 5678"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={developerForm.email}
                  onChange={(e) => setDeveloperForm({...developerForm, email: e.target.value})}
                  placeholder="info@developer.com"
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={developerForm.website}
                  onChange={(e) => setDeveloperForm({...developerForm, website: e.target.value})}
                  placeholder="www.developer.com"
                />
              </div>
            </div>
            
            <div>
              <Label>Address</Label>
              <Input
                value={developerForm.address}
                onChange={(e) => setDeveloperForm({...developerForm, address: e.target.value})}
                placeholder="Corporate office address"
              />
            </div>
            
            <Separator />
            
            <div>
              <Label className="text-base font-medium">Contact Person</Label>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={developerForm.contact_person}
                  onChange={(e) => setDeveloperForm({...developerForm, contact_person: e.target.value})}
                  placeholder="Contact name"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={developerForm.contact_person_phone}
                  onChange={(e) => setDeveloperForm({...developerForm, contact_person_phone: e.target.value})}
                  placeholder="Contact phone"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={developerForm.contact_person_email}
                  onChange={(e) => setDeveloperForm({...developerForm, contact_person_email: e.target.value})}
                  placeholder="Contact email"
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={developerForm.notes}
                onChange={(e) => setDeveloperForm({...developerForm, notes: e.target.value})}
                placeholder="Additional notes about this developer"
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDeveloperDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveDeveloper} className="bg-accent hover:bg-accent/90" data-testid="save-developer-btn">
                {editingDeveloper ? 'Update' : 'Create'} Developer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
            <DialogDescription>
              {selectedDeveloper && `Project under ${selectedDeveloper.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                placeholder="e.g., Prestige Lakeside Habitat"
                data-testid="project-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Type</Label>
                <Select
                  value={projectForm.project_type}
                  onValueChange={(value) => setProjectForm({...projectForm, project_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Residential">Residential</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Mixed Use">Mixed Use</SelectItem>
                    <SelectItem value="Villa">Villa</SelectItem>
                    <SelectItem value="Plotted">Plotted Development</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={projectForm.status}
                  onValueChange={(value) => setProjectForm({...projectForm, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="sold_out">Sold Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location</Label>
                <Input
                  value={projectForm.location}
                  onChange={(e) => setProjectForm({...projectForm, location: e.target.value})}
                  placeholder="e.g., Whitefield"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={projectForm.city}
                  onChange={(e) => setProjectForm({...projectForm, city: e.target.value})}
                  placeholder="e.g., Bangalore"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Units</Label>
                <Input
                  type="number"
                  value={projectForm.total_units}
                  onChange={(e) => setProjectForm({...projectForm, total_units: e.target.value})}
                  placeholder="e.g., 500"
                />
              </div>
              <div>
                <Label>Available Units</Label>
                <Input
                  type="number"
                  value={projectForm.available_units}
                  onChange={(e) => setProjectForm({...projectForm, available_units: e.target.value})}
                  placeholder="e.g., 150"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price Range (Min) AED</Label>
                <Input
                  type="number"
                  value={projectForm.price_range_min}
                  onChange={(e) => setProjectForm({...projectForm, price_range_min: e.target.value})}
                  placeholder="e.g., 8000000"
                />
              </div>
              <div>
                <Label>Price Range (Max) AED</Label>
                <Input
                  type="number"
                  value={projectForm.price_range_max}
                  onChange={(e) => setProjectForm({...projectForm, price_range_max: e.target.value})}
                  placeholder="e.g., 25000000"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Possession Date</Label>
                <Input
                  value={projectForm.possession_date}
                  onChange={(e) => setProjectForm({...projectForm, possession_date: e.target.value})}
                  placeholder="e.g., Dec 2027"
                />
              </div>
              <div>
                <Label>RERA Number</Label>
                <Input
                  value={projectForm.rera_number}
                  onChange={(e) => setProjectForm({...projectForm, rera_number: e.target.value})}
                  placeholder="Project RERA"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={projectForm.description}
                onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                placeholder="Project description"
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowProjectDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveProject} className="bg-accent hover:bg-accent/90" data-testid="save-project-btn">
                {editingProject ? 'Update' : 'Create'} Project
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeveloperMaster;
